import { CalendarIcon, LockClosedIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { Draggable } from '@hello-pangea/dnd';
import { Tooltip } from 'react-tooltip';
import { L } from '@execonline-inc/translations';
import { T } from '../../../../../../Translations';
import { ExperienceResource } from '../Types';
import { isUnlicensed } from './utils';
import OnDemandProgramIcon from '../ExperienceFooter/ExperienceProductBadge/Icons/OnDemandProgramIcon';
import ScheduledProgramIcon from '../ExperienceFooter/ExperienceProductBadge/Icons/ScheduledProgramIcon';
import { isScheduledCourse } from '../ExperienceFooter/ExperienceProductBadge/isScheduledCourse';

// GripVertical icon (matches lucide-react design)
const GripVertical = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="9" cy="12" r="1" />
    <circle cx="9" cy="5" r="1" />
    <circle cx="9" cy="19" r="1" />
    <circle cx="15" cy="12" r="1" />
    <circle cx="15" cy="5" r="1" />
    <circle cx="15" cy="19" r="1" />
  </svg>
);

interface DraggableProgramProps {
  experience: ExperienceResource;
  index: number;
  draggableId: string;
}

function DraggableProgram({ experience, index, draggableId }: DraggableProgramProps) {
  const { title, product_license_type_ids, program_instance_options } = experience.payload;

  const unlicensed = isUnlicensed(product_license_type_ids);
  const scheduledCount = program_instance_options?.scheduled?.length ?? 0;
  const isScheduled = isScheduledCourse(experience.payload);
  const ProgramIcon = isScheduled ? ScheduledProgramIcon : OnDemandProgramIcon;

  return (
    <Draggable draggableId={draggableId} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`bg-card border rounded-md p-4 cursor-move transition-opacity ${
            snapshot.isDragging ? 'opacity-50' : 'opacity-100'
          } ${unlicensed ? 'border-muted-foreground/30' : 'border-border'}`}
          data-intercom-target="builder-v2-drawer-program-item"
        >
          <div className="flex items-start gap-3">
            {/* Drag Handle */}
            <div
              {...provided.dragHandleProps}
              className="flex-shrink-0"
              data-intercom-target="program-reorder-handle"
            >
              <GripVertical
                className={`w-5 h-5 ${unlicensed ? 'text-muted-foreground/50' : 'text-muted-foreground'}`}
              />
            </div>

            {/* Program Content */}
            <div className={`flex-1 ${unlicensed ? 'opacity-60' : ''}`}>
              {/* Program Name and Icons */}
              <div className="flex items-center gap-2">
                <h5 style={{ fontWeight: 'var(--font-weight-medium)' }}>{title}</h5>

                {/* Program Type Icon */}
                <div data-tooltip-id={`type-${experience.payload.id}`} className="flex-shrink-0">
                  <ProgramIcon size="md" />
                </div>
                <Tooltip
                  id={`type-${experience.payload.id}`}
                  place="bottom"
                  opacity={1}
                  style={{
                    backgroundColor: 'var(--foreground)',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    zIndex: 1000,
                  }}
                >
                  <p className="text-primary-foreground" style={{ fontSize: 'var(--text-xs)' }}>
                    {isScheduled ? <T kind="Scheduled Course" /> : <T kind="On-Demand Course" />}
                  </p>
                </Tooltip>

                {/* Lock Icon for Unlicensed */}
                {unlicensed && (
                  <>
                    <div
                      data-tooltip-id={`unlicensed-${experience.payload.id}`}
                      data-intercom-target="builder-v2-drawer-program-unlicensed-icon"
                      className="cursor-help"
                    >
                      <LockClosedIcon
                        className="w-4 h-4 text-muted-foreground"
                        aria-hidden="true"
                      />
                    </div>
                    <Tooltip
                      id={`unlicensed-${experience.payload.id}`}
                      place="bottom"
                      opacity={1}
                      style={{
                        backgroundColor: 'var(--foreground)',
                        borderRadius: '8px',
                        padding: '12px',
                        maxWidth: '280px',
                        zIndex: 1000,
                      }}
                    >
                      <div className="flex items-center justify-center gap-2 mb-1">
                        <XCircleIcon className="h-5 w-5 text-destructive" aria-hidden="true" />
                        <p
                          className="text-primary-foreground"
                          style={{
                            fontSize: 'var(--text-xs)',
                            fontWeight: 'var(--font-weight-medium)',
                          }}
                        >
                          <T kind="Not Available" />
                        </p>
                      </div>
                      <p
                        className="text-primary-foreground/70 mt-1"
                        style={{ fontSize: 'var(--text-xs)' }}
                      >
                        <T kind="This program type is not included in your current license entitlement" />
                      </p>
                    </Tooltip>
                  </>
                )}
              </div>

              {/* Scheduled Dates */}
              {scheduledCount > 0 && (
                <div className="flex items-center gap-2 mt-1">
                  <CalendarIcon className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
                  <div data-tooltip-id={`dates-${experience.payload.id}`}>
                    <span
                      className="text-muted-foreground cursor-default"
                      style={{ fontSize: 'var(--text-sm)' }}
                    >
                      {scheduledCount}{' '}
                      {scheduledCount === 1 ? <T kind="date" /> : <T kind="dates" />}
                    </span>
                  </div>
                  <Tooltip
                    id={`dates-${experience.payload.id}`}
                    place="bottom"
                    opacity={1}
                    style={{
                      backgroundColor: 'var(--foreground)',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      zIndex: 1000,
                    }}
                  >
                    <div className="space-y-1">
                      {program_instance_options?.scheduled?.map((date) => (
                        <p
                          key={date.id}
                          className="text-primary-foreground"
                          style={{ fontSize: 'var(--text-xs)' }}
                        >
                          <L localizeable={date.start_on} format="long-weekday-month-year" />
                        </p>
                      ))}
                    </div>
                  </Tooltip>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Draggable>
  );
}

export default DraggableProgram;
